import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  TableContainer,
  Paper,
  Box,
  Tabs,
  Tab,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  CircularProgress,
  IconButton,
  DialogActions,
  TextField,
} from "@mui/material";
import { DialogTitle } from "@mui/material";
import { Checkbox } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  StyledButton,
  StyledCancelButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainerMis,
  StyledAgGridContainer,
  StyledTableContainer,
  StyledTypographyA,
  inputStyle,
  DeleteButton,
} from "components/StyledComponents/styledComponents.js";
import { globalStyles } from "components/Styles/globalStyles";
import URLs from "constants/urls";
import { getPresignedUrl } from "constants/urls";
import Cookie from "js-cookie";
import dayjs from "dayjs";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import { CheckCircle, Visibility, CloudUpload, Download } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import { useMaterialUIController } from "context";

// ✅ Base URL — update this to match your mainBaseUrl
const mainBaseUrl = "https://fintrack-java-api.kotaholdings.in/business/fintrack";

export default function MISReport() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [misData, setMisData] = useState([]);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const MInput = inputStyle();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const jwt_token = Cookie.get("jwt_token");
  const [paramsData, setParamsData] = useState({});
  const [openFileFolder, setOpenFileFolder] = useState(false);
  const [file, setFile] = useState(null);
  const [misDialogOpen, setMisDialogOpen] = useState(false);
  const [misDialogAction, setMisDialogAction] = useState(null);
  const [misDialogMessage, setMisDialogMessage] = useState("");

  const today = dayjs();

  const financialYearStart =
    today.month() >= 3
      ? dayjs(`${today.year()}-04-01`).format("YYYY-MM-DD")
      : dayjs(`${today.year() - 1}-04-01`).format("YYYY-MM-DD");

  const currentMonthEnd = today.endOf("month").format("YYYY-MM-DD");

  // ─── Alert helpers ───────────────────────────────────────────────────────────
  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // ─── MIS confirmation dialog ─────────────────────────────────────────────────
  const handleOpenMisDialog = (action, message) => {
    if (selectedEntryIds.length === 0) {
      setAlert("Please select applications", "error");
      return;
    }
    setMisDialogAction(() => action);
    setMisDialogMessage(message);
    setMisDialogOpen(true);
  };

  const handleCloseMisDialog = () => {
    setMisDialogOpen(false);
    setMisDialogAction(null);
    setMisDialogMessage("");
  };

  const handleConfirmMisDialog = () => {
    if (misDialogAction) misDialogAction();
    handleCloseMisDialog();
  };

  // ─── Checkbox selection ───────────────────────────────────────────────────────
  const handleCheckboxClick = (entryId) => {
    setSelectedEntryIds((prev) => {
      const updatedIds = prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId];
      setSelectedRows(misData.filter((row) => updatedIds.includes(row.entryId)));
      return updatedIds;
    });
  };

  const handleSelectAll = () => {
    if (selectedEntryIds.length === misData.length) {
      setSelectedEntryIds([]);
      setSelectedRows([]);
    } else {
      setSelectedEntryIds(misData.map((row) => row.entryId)); // ✅ numeric entryId
      setSelectedRows(misData);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getDate()}-${date.toLocaleString("en-US", {
      month: "short",
    })}-${date.getFullYear()}`;
  };

  // ─── Custom header with Select All checkbox ──────────────────────────────────
  const HeaderCheckbox = () => (
    <>
      <Checkbox
        checked={selectedEntryIds.length === misData.length && misData.length > 0}
        indeterminate={selectedEntryIds.length > 0 && selectedEntryIds.length < misData.length}
        onChange={handleSelectAll}
      />
      <DeleteButton sx={{ fontWeight: "bold", borderRadius: "10px" }}>Select All</DeleteButton>
    </>
  );

  // ─── Column definitions — mapped to NEW API response fields ──────────────────
  const columns = [
    {
      field: "addToMIS",
      headerName: "Select All",
      headerComponent: HeaderCheckbox,
      align: "center",
      width: 150,
      cellRenderer: (params) => {
        if (params.node.rowPinned === "bottom") return null;
        return (
          <>
            <Checkbox
              checked={selectedEntryIds.includes(params.data.entryId)} // ✅ entryId
              onChange={() => handleCheckboxClick(params.data.entryId)} // ✅ entryId
            />
            {activeTab === 1 && (
              <>
                <Tooltip title="Upload" arrow>
                  <IconButton onClick={() => handleOpenDialogFileUploader(params)} color="primary">
                    <CloudUpload />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download" arrow>
                  <IconButton onClick={() => triggerS3File(params)} color="primary">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
    {
      field: "date",
      headerName: "Date",
      sortable: true,
      filter: true,
      width: 130,
      valueFormatter: (params) => formatDate(params.value),
      sort: "desc",
    },
    {
      field: "applicationNumber", // ✅ same field name
      headerName: "Appl. No.",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      field: "customerName", // ✅ same field name
      headerName: "Customer Name",
      sortable: true,
      filter: true,
      flex: 1.5,
    },
    {
      field: "entry Id", // ✅ same field name
      headerName: "entryId",
      sortable: true,
      filter: true,
      flex: 1.5,
      hide: true,
    },
    {
      field: "businessType", // ✅ was loanType → now businessType
      headerName: "Business Type",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      field: "dsaName", // ✅ same field name
      headerName: "DSA Name",
      sortable: true,
      filter: true,
    },
    {
      field: "providerName", // ✅ was bankName → now providerName
      headerName: "Provider Name",
      sortable: true,
      filter: true,
    },
    {
      field: "appliedAmount", // ✅ was loanAmount → now appliedAmount
      headerName: "Applied Amount",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}`,
    },
    {
      field: "grossPayoutPercent", // ✅ was revenuePercentage → now grossPayoutPercent
      headerName: "Gross Payout (%)",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        params.value !== undefined && params.value !== null ? `${params.value.toFixed(2)}%` : "",
    },
    {
      field: "dsaPayoutPercent", // ✅ was dsaPayoutPercentage → now dsaPayoutPercent
      headerName: "DSA Pay-out (%)",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        params.value !== undefined && params.value !== null ? `${params.value.toFixed(2)}%` : "",
    },
    // ✅ Removed: revenue (Actual Amount) & dsaPayoutValue — not in new API response
    // Add them back if the backend adds those fields later
  ];

  // ─── File upload/download handlers ───────────────────────────────────────────
  const handleOpenDialogFileUploader = (params) => {
    setParamsData(params.data);
    setOpenFileFolder(true);
  };

  const handleCloseDialogFileUploader = () => {
    setOpenFileFolder(false);
    setFile(null);
  };

  const handleFileChange = (event) => setFile(event.target.files[0]);

  const triggerS3File = async (params) => {
    setParamsData();
    const url = getPresignedUrl({
      fileName: params.data.applicationNumber,
      group: "mis",
      env: "stage",
      type: "download",
    });
    try {
      const downloadResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (downloadResponse.ok) {
        const presignedUrl = await downloadResponse.text();
        if (!presignedUrl.startsWith("http")) throw new Error("Invalid Pre-Signed URL received");
        if (presignedUrl) window.open(presignedUrl, "_blank");
      }
    } catch {
      setAlert("Failed to Download", "error");
    }
  };

  const uploadFileToS3 = async () => {
    if (!file) {
      setAlert("Please select a file first.", "error");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".eml")) {
      setAlert("Only .eml files are allowed!", "error");
      return;
    }
    const uploadUrl = getPresignedUrl({
      fileName: paramsData.applicationNumber,
      group: "mis",
      env: "stage",
      type: "upload",
    });
    setLoading(true);
    try {
      const response = await fetch(uploadUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${jwt_token}` },
      });
      if (!response.ok) throw new Error("Failed to get pre-signed URL");
      const presignedUrl = await response.text();
      if (!presignedUrl.startsWith("http")) throw new Error("Invalid Pre-Signed URL received");

      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "message/rfc822" },
      });
      if (uploadResponse.ok) {
        setAlert("File uploaded successfully!", "success");
        setOpenFileFolder(false);
      } else {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }
    } catch (error) {
      setAlert("File upload failed.", "error");
    }
    setLoading(false);
  };

  // ─── Fetch MIS data — UPDATED to new API & new URL format ────────────────────
  const fetchMisData = async (misStatus) => {
    if (!user) return;
    setLoading(true);
    try {
      const apiUrl = `${URLs.dsaMISByDsaId}?dsaId=${user.companyId}&misStatus=${misStatus}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        setAlert(`Failed to fetch MIS data: ${response.status}`, "error");
        return;
      }

      const data = await response.json();
      setMisData(data);
    } catch (error) {
      setAlert("An error occurred while fetching data", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!user) return;
    const misStatus = activeTab === 0 ? "N" : "I";
    fetchMisData(misStatus);
    setSelectedEntryIds([]);
    setSelectedRows([]);
  }, [activeTab, user]);

  // ─── Add to MIS (Tab 0 → move to misStatus I) ────────────────────────────────
  const handleAddToMIS = async () => {
    if (selectedEntryIds.length === 0) {
      setAlert("Please select applications", "error");
      return;
    }

    const bodyData = {
      entryIds: [...selectedEntryIds], // applicationNumbers used as IDs
      misStatus: "I",
    };
    console.log("Payload:", bodyData); // ✅ check what's being sent
    console.log("Token:", jwt_token);

    try {
      setLoading(true);
      const response = await fetch(URLs.entryMisStateUpdateUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(bodyData),
      });
      const responseText = await response.text();
      if (response.ok) {
        setAlert(responseText, "success");
        fetchMisData("N");
      } else {
        setAlert(`Failed to update data: ${responseText}`, "error");
      }
    } catch {
      setAlert("An error occurred while updating data", "error");
    } finally {
      setLoading(false);
    }
  };

  // ─── Approve MIS (Tab 1 → move to misStatus C) ───────────────────────────────
  const downloadAndSubmitSelectedRows = async () => {
    if (selectedEntryIds.length === 0) {
      setAlert("Please select at least one row to approve.", "error");
      return;
    }

    const bodyData = {
      entryIds: [...selectedEntryIds],
      misStatus: "C",
    };

    try {
      setLoading(true);
      const response = await fetch(URLs.entryMisStateUpdateUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(bodyData),
      });
      const responseText = await response.text();
      if (response.ok) {
        setAlert(responseText, "success");
        fetchMisData("I");
      } else {
        setAlert(`Failed to update data: ${responseText}`, "error");
      }
    } catch {
      setAlert("An error occurred while updating data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => setActiveTab(newValue);

  const tabs = [{ label: "Entries" }, { label: "Approve MIS" }];

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      {/* Loading dialog */}
      <Dialog
        open={loading}
        onClose={null}
        sx={{ backgroundColor: "transparent", boxShadow: "none" }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            boxShadow: "none",
          }}
        >
          <CircularProgress color="primary" />
          <Typography variant="h6" sx={{ marginTop: 2, color: "white" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* File upload dialog */}
      <Dialog open={openFileFolder} onClose={handleCloseDialogFileUploader}>
        <StyledTypographyA mt={2}>Upload File</StyledTypographyA>
        <DialogContent>
          <TextField
            sx={MInput.b}
            InputLabelProps={{ sx: { color: MInput.a } }}
            type="file"
            onChange={handleFileChange}
          />
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseDialogFileUploader} color="secondary">
            Cancel
          </StyledButton>
          <StyledButton onClick={uploadFileToS3} color="primary" variant="contained">
            Submit
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      <StyledTableContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            overflow: "hidden",
            flexGrow: 1,
            boxShadow: "none",
            border: "none",
            backgroundColor: "transparent",
          }}
        >
          <ResponsiveTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={handleChange}
            labelColor="#ffffff"
            bgColor="#e0f7fa"
          />

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              mt: 0,
              p: 0,
              pt: 2,
              pr: 0,
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "none",
            }}
          >
            {/* Tab 0 — Entries */}
            {activeTab === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <StyledButton
                    onClick={() =>
                      handleOpenMisDialog(handleAddToMIS, "Are you sure you want to add to MIS?")
                    }
                    disabled={loading}
                  >
                    Add to MIS
                  </StyledButton>
                </Box>
                <StyledAgGridContainer
                  component={Paper}
                  className="ag-theme-alpine"
                  sx={{
                    flexGrow: 1,
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  <AgGridReact
                    rowData={misData}
                    columnDefs={columns}
                    pagination={true}
                    paginationPageSize={11}
                  />
                </StyledAgGridContainer>
              </Box>
            )}

            {/* Tab 1 — Approve MIS */}
            {activeTab === 1 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <StyledButton
                    onClick={() =>
                      handleOpenMisDialog(
                        downloadAndSubmitSelectedRows,
                        "Are you sure you want to approve?"
                      )
                    }
                    disabled={loading}
                  >
                    Approve
                  </StyledButton>
                </Box>
                <StyledAgGridContainer
                  component={Paper}
                  className="ag-theme-alpine"
                  sx={{
                    flexGrow: 1,
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  <AgGridReact
                    rowData={misData}
                    columnDefs={columns}
                    pagination={true}
                    paginationPageSize={11}
                  />
                </StyledAgGridContainer>
              </Box>
            )}

            {/* Confirmation dialog */}
            <Dialog open={misDialogOpen} onClose={handleCloseMisDialog}>
              <DialogContent sx={{ mt: 1, ...subDialogContentStyle }}>
                {misDialogMessage || "Are you sure you want to proceed with this action?"}
              </DialogContent>
              <DialogActions>
                <StyledCancelButton onClick={handleCloseMisDialog} color="secondary">
                  Cancel
                </StyledCancelButton>
                <StyledButton onClick={handleConfirmMisDialog} color="primary">
                  Confirm
                </StyledButton>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </StyledTableContainer>
    </DashboardLayout>
  );
}
